import React from 'react';
import type { Tone } from '@/lib/dict';

/**
 * Основа для всех бейджей состояния.
 *
 * Один примитив на все словари: значений в них полтора десятка, и разводить
 * под каждое свой набор классов — гарантированный способ получить два разных
 * серых на соседних колонках.
 */

const toneClasses: Record<Tone, string> = {
  success: 'bg-green-50 text-green-700 border-green-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  /** Событие произошло, но оно не хорошее и не плохое. */
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  /** Значения нет либо оно неизвестно. Тише остальных намеренно. */
  muted: 'bg-slate-50 text-slate-500 border-slate-200',
};

export type BadgeSize = 'sm' | 'md' | 'lg';

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 gap-0.5',
  md: 'text-xs px-2 py-0.5 gap-1',
  lg: 'text-sm px-3 py-1 gap-1.5',
};

export interface BadgeProps {
  tone: Tone;
  size?: BadgeSize;
  icon?: React.ReactNode;
  /** Подсказка при наведении: словари часто требуют пояснения. */
  title?: string;
  mono?: boolean;
  children: React.ReactNode;
}

export default function Badge({
  tone,
  size = 'md',
  icon,
  title,
  mono = false,
  children,
}: BadgeProps) {
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full border font-medium whitespace-nowrap ${toneClasses[tone]} ${sizeClasses[size]} ${mono ? 'font-mono' : ''}`}
    >
      {icon}
      {children}
    </span>
  );
}
